<template>
    <section class="doc-section">

        <!-- Selector de Plataforma -->
        <div class="mb-6">
            <BotonesNavegacion @cambio-plataforma="plataformaActual = $event" />
        </div>

        <header class="header-container">
            <h1>Ubicación Actual del Usuario</h1>
            <p class="description">
                Vista que permite visualizar su posición en tiempo real dentro del mapa de
                monitoreo mediante el uso de la geolocalización del navegador.
            </p>
        </header>

        <div class="two-col">
            <div class="col-steps">
                <p class="steps-label">Proceso de Geolocalización</p>

                <!-- WEB -->
                <template v-if="plataformaActual === 'web'">
                    <div class="step-card">
                        <div class="step-badge">1</div>
                        <div>
                            <div class="step-title">Permisos del navegador</div>
                            <div class="step-desc">
                                Al activar la función, el navegador solicitará autorización para acceder a su ubicación actual.
                            </div>
                        </div>
                    </div>

                    <div class="step-card">
                        <div class="step-badge">2</div>
                        <div>
                            <div class="step-title">Visualización en mapa</div>
                            <div class="step-desc">
                                Una vez autorizada, el sistema centrará el mapa y mostrará su posición mediante un marcador distintivo.
                            </div>
                        </div>
                    </div>
                </template>

                <!-- MÓVIL -->
                <template v-else>
                    <div class="step-card">
                        <div class="step-badge">1</div>
                        <div>
                            <div class="step-title">Activar ubicación</div>
                            <div class="step-desc">
                                Presione el icono de ubicación dentro del mapa para iniciar la geolocalización.
                            </div>
                        </div>
                    </div>

                    <div class="step-card">
                        <div class="step-badge">2</div>
                        <div>
                            <div class="step-title">Autorizar permisos</div>
                            <div class="step-desc">
                                El sistema solicitará autorización para acceder a la ubicación del dispositivo móvil.
                            </div>
                        </div>
                    </div>

                    <div class="step-card">
                        <div class="step-badge">3</div>
                        <div>
                            <div class="step-title">Visualización en mapa</div>
                            <div class="step-desc">
                                Una vez autorizada, el sistema centrará el mapa y mostrará su posición mediante un marcador distintivo.
                            </div>
                        </div>
                    </div>
                </template>

                <div class="doc-note note-info">
                    <svg class="note-icon" width="15" height="15" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M8 5v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        <circle cx="8" cy="11.5" r="0.75" fill="currentColor"/>
                    </svg>

                    <div class="note-body">
                        <span class="note-label">Privacidad</span>
                        <p>La ubicación solo es visible para usted y el navegador cifra la transmisión de los datos de coordenadas.</p>
                    </div>
                </div>
            </div>

            <div class="col-image">
                <transition name="fade" mode="out-in">
                    <CarruselAciqui
                        :key="plataformaActual"
                        :items="imagenesUbicacionMostrar"
                    />
                </transition>
            </div>
        </div>

        <div class="doc-note note-warning">
            <svg class="note-icon" width="15" height="15" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                <path d="M8 5v3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                <circle cx="8" cy="10.5" r="0.75" fill="currentColor"/>
            </svg>
            <div class="note-body">
                <span class="note-label">Precisión de ubicación</span>
                <p>Asegúrese de tener habilitado el GPS o los servicios de ubicación en la configuración de su sistema operativo para garantizar la exactitud de la posición.</p>
            </div>
        </div>

    </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import CarruselAciqui from '../Carruselimg.vue';
import BotonesNavegacion from '../BotonesNavegacion.vue';

const plataformaActual = ref('web');

/* ── Datos Web ───────────────────────────────────── */
const webData = {
    imagenes: [
        {
            nombre: "Ubicación del Usuario",
            url: '/img/ubicaciones/Ubicacion_Actual_Usuario.png',
            descripcion: "Marcador de posición actual del usuario en el mapa de monitoreo."
        }
    ]
};

/* ── Datos Móvil ─────────────────────────────────── */
const mobileData = {
    imagenes: [
        {
            nombre: "Activar ubicación",
            url: '/img/ubicaciones/Ubicacion_Movil_1.png',
            descripcion: "Presionar el icono de ubicación dentro del mapa."
        },
        {
            nombre: "Permisos de ubicación",
            url: '/img/ubicaciones/Ubicacion_Movil_2.png',
            descripcion: "Solicitud de permisos del sistema para acceder a la ubicación."
        },
        {
            nombre: "Permisos autorizados",
            url: '/img/ubicaciones/Ubicacion_Movil_3.png',
            descripcion: "Confirmación de autorización de acceso a ubicación."
        },
        {
            nombre: "Ubicación en mapa",
            url: '/img/ubicaciones/Ubicacion_Movil_4.png',
            descripcion: "Marcador visible de la ubicación actual del usuario."
        }
    ]
};

/* ── Computed ────────────────────────────────────── */
const imagenesUbicacionMostrar = computed(() =>
    plataformaActual.value === 'web'
        ? webData.imagenes
        : mobileData.imagenes
);
</script>

<style scoped>
.mb-6 {
    margin-bottom: 2rem;
}

/* ── Animación ───────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
/* ── Estilos Base ─────────────────────────────────── */
.doc-section { padding: 20px; }
.description { font-size: 15px; opacity: 0.8; margin-bottom: 24px; line-height: 1.6; }

/* ── Layout dos columnas ──────────────────────────── */
.two-col { display: flex; gap: 32px; align-items: flex-start; margin-bottom: 32px; }
.col-steps { flex: 1; }
.col-image  { flex: 1; position: sticky; top: 20px; }

/* ── Pasos y Cards (Modo Oscuro) ──────────────────── */
.steps-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--primary-color);
    margin-bottom: 12px;
}

.step-card {
    display: flex; gap: 14px; align-items: flex-start;
    background: var(--code-bg); border: 1px solid var(--border-color);
    border-radius: 10px; padding: 14px 16px; margin-bottom: 10px;
}

.step-badge {
    min-width: 28px; height: 28px; border-radius: 50%;
    background: var(--primary-color); color: #fff;
    font-size: 13px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

.step-title { font-size: 14px; font-weight: 600; margin-bottom: 4px; }
.step-desc { font-size: 13px; opacity: 0.8; line-height: 1.5; }

/* ── UI Elements ─────────────────────────────────── */
.section-divider { display: flex; align-items: center; gap: 12px; margin: 32px 0 16px; }
.section-divider h2 { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.6; margin: 0; }
.divider-line { flex: 1; height: 1px; background: var(--border-color); }

.carousel-box { 
    border: 1px solid var(--border-color); 
    padding: 8px; 
    border-radius: 10px; 
    background: var(--code-bg); 
    margin-bottom: 16px; 
}

/* ── Sistema de Notas (Dark Mode Ready) ───────────── */
.doc-note { display: flex; gap: 12px; padding: 16px; border-radius: 8px; border: 1px solid; margin: 16px 0; font-size: 14px; }
.note-icon { flex-shrink: 0; margin-top: 2px; }
.note-label { font-weight: 700; text-transform: uppercase; font-size: 11px; display: block; margin-bottom: 2px; }

/* Nota informativa - Azul */
.note-info { background: var(--code-bg); border-color: var(--primary-color); color: var(--text-color); }
.note-info .note-label { color: var(--primary-color); }

/* Nota advertencia - Ámbar */
.note-warning { background: var(--code-bg); border-color: #d97706; color: var(--text-color); }
.note-warning .note-label { color: #d97706; }

/* ── Responsive ──────────────────────────────────── */
@media (max-width: 768px) {
    .two-col { flex-direction: column; }
    .col-image { position: static; }
}
</style>